import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "uv-filters-top-wrapper" }
const _hoisted_2 = { class: "uv-filters-total" }
const _hoisted_3 = { class: "uv-filters-btns" }

import { computed, ref } from 'vue';
	import { useStore } from 'vuex';
	import FiltersToggle from '@/components/FiltersToggle.vue';
	import FilterMultiSelect from './FilterMultiSelect.vue';
	import FilterRange from './FilterRange.vue';
	import { 
		IFilterMultiSelectOption, 
		ILocation, 
		TOptionValue, 
		IFilterOption,
		IStoreState
	} from '@/types';
	import { EFilterNames } from '@/enums';
	import RouterApp from '@/RouterApp.vue';
	import { toTitleCase, getPriceFormat, getYearFormat, getYearFormatPre } from '@/helpers';

	
export default /*#__PURE__*/_defineComponent({
  setup(__props) {

	const store = useStore<IStoreState>();


	const allowLocationSelection: boolean = (window as any).uvFilterInit.FilterLocations.length > 1;
	const certifiedTitle: string = (window as any).uvCertifiedTitle;
	const initialPriceMin = (window as any).uvFilterInit.PriceMin;
	const initialPriceMax = (window as any).uvFilterInit.PriceMax;
	const initialYearMin = (window as any).uvFilterInit.YearMin;
	const initialYearMax = (window as any).uvFilterInit.YearMax;

	const hasRouterFinishedPreloading = ref(false);

	const isRetrievingData = computed(() => store.state.isRetrievingData);
	const isLoading = computed(() => {
		return isRetrievingData.value || !hasRouterFinishedPreloading.value;
	});
	const panelOpenClass =  computed((): string => {
		return `uv-filters ${store.state.isFilterOpen ? 'open' : ''} ${isLoading.value ? 'loading' : ''}`;
	});
	const totalVehicles =  computed((): number => store.state.totalVehicles);
	const totalVehiclesText = computed((): string => `${store.state.totalVehicles === 1 ? "Car" : "Cars"} for sale at ${(window as any).uvDealerName}`);
	const selectedPriceMin = computed((): number => store.state.filters?.priceMin || initialPriceMin);
	const selectedPriceMax = computed((): number => store.state.filters?.priceMax || initialPriceMax);
	const hasPreYearValue = computed((): boolean => store.state.hasPreYearValue);
	const preYearValue = computed((): number => initialYearMin - 1);
	const finalInitialYearMin = computed((): number => hasPreYearValue.value ? preYearValue.value : initialYearMin);
	const selectedYearMin = computed((): number => store.state.filters?.yearMin || finalInitialYearMin.value);
	const selectedYearMax = computed((): number => store.state.filters?.yearMax || initialYearMax);
	const yearFormatModifier = computed((): any => hasPreYearValue.value ? getYearFormatPre : getYearFormat);

	const allowConditionSelection = computed((): boolean => {
		return store.state.filters?.conditions.length > 1;
	});
	const allowCertifiedSelection = computed((): boolean => 
		store.state.filters?.certified.filter((o: IFilterOption) => o.Selected).length || 
		store.state.filters?.certified.length// only yes
	);
	const allowModelSelection = computed((): boolean => 
		store.state.filters?.models.filter((o: IFilterOption) => o.Selected).length || 
		store.state.filters?.models.length > 1
	);
	const allowBodySelection = computed((): boolean => 
		store.state.filters?.bodies.filter((o: IFilterOption) => o.Selected).length || 
		store.state.filters?.bodies.length > 1
	);
	const allowFuelSelection = computed((): boolean => 
		store.state.filters?.fuels.filter((o: IFilterOption) => o.Selected).length || 
		store.state.filters?.fuels.length > 1
	);
	const allowTransmissionSelection = computed((): boolean => 
		store.state.filters?.transmissions.filter((o: IFilterOption) => o.Selected).length || 
		store.state.filters?.transmissions.length > 1
	);

	const conditionOptions = computed((): Array<IFilterMultiSelectOption<number>> => convertLocationOptionToSelectOption(store.state.filters?.conditions || []));

	const locationOptions = computed((): Array<IFilterMultiSelectOption<number>> => convertLocationOptionToSelectOption(store.state.filters?.locations || []));

	const makeOptions = computed((): Array<IFilterMultiSelectOption<string>> => convertFilterOptionToSelectOption(store.state.filters?.makes || []));

	const certifiedOptions = computed((): Array<IFilterMultiSelectOption<string>> => convertFilterOptionToSelectOption(store.state.filters?.certified || []));

	const modelOptions = computed((): Array<IFilterMultiSelectOption<string>> => convertFilterOptionToSelectOption(store.state.filters?.models || []));

	const bodyOptions = computed((): Array<IFilterMultiSelectOption<string>> => convertFilterOptionToSelectOption(store.state.filters?.bodies || []));

	const fuelOptions = computed((): Array<IFilterMultiSelectOption<string>> => convertFilterOptionToSelectOption(store.state.filters?.fuels || []));

	const transmissionOptions = computed((): Array<IFilterMultiSelectOption<string>> => convertFilterOptionToSelectOption(store.state.filters?.transmissions || []));

	const allowFilter = (parent: Array<IFilterOption>, child: Array<IFilterOption>): boolean => {
		return !!(
			parent?.filter((o: IFilterOption) => o.Selected).length && 
			(child?.filter((o: IFilterOption) => o.Selected).length || child?.length > 0)
		);
	}
	const convertFilterOptionToSelectOption = (arr: Array<IFilterOption>): Array<IFilterMultiSelectOption<any>> => {
		return arr.map((o:IFilterOption) => {
			return {
				text: toTitleCase(o.Item), 
				value: o.Item, 
				selected: o.Selected, 
				count: o.Count
			};
		});
	};
	const convertLocationOptionToSelectOption = (arr: Array<ILocation>): Array<IFilterMultiSelectOption<any>> => {
		return arr.map((o:ILocation) => {
			return {
				text: toTitleCase(o.LocationName), 
				value: o.LocationId, 
				selected: o.Selected, 
				count: o.Count
			};
		});
	};
	
	const handleRouterAppReady = (v: boolean) => {
		hasRouterFinishedPreloading.value = v;
	}
	const handleNewSearchClick = ($event: Event): void => {
		$event.preventDefault();
		(window as any).location = (window as any).uvFilterInit.NewSearchUrl;
	};
	const resetFilter = (dependentFilter: Array<IFilterOption>, commitName: string) => {
		if (!dependentFilter) return;
		const dependents = getSelectedFalse(dependentFilter);
		store.commit(`filters/${commitName}`, dependents);
	};
	const getSelectedFalse = (arr2: Array<IFilterOption>): Array<IFilterOption> => {
		const arr = [...arr2];
		arr.forEach((o: IFilterOption) => {
			o.Selected = false;
		});
		return arr;
	}
	const handleFilterChange = (val: Array<TOptionValue>, filterName: EFilterNames) => {
		const f = store.state.filters;
		switch (filterName){
			case EFilterNames.condition: {
				const arr = [...f?.conditions];
				arr.forEach((o: ILocation) => {
					o.Selected = val.includes(o.LocationId);
				});
				store.commit('filters/setConditions', arr);
				break;
			}
			case EFilterNames.location: {
				const arr = [...f?.locations];
				arr.forEach((o: ILocation) => {
					o.Selected = val.includes(o.LocationId);
				});
				store.commit('filters/setLocations', arr);
				break;
			} 
			case EFilterNames.make: {
				const arr = JSON.parse(JSON.stringify(f?.makes));
				arr.forEach((o: IFilterOption) => {
					o.Selected = val.includes(o.Item);
				});
				resetFilter(f?.models, 'setModels');
				store.commit('filters/setMakes', arr);
				break;
			}
			case EFilterNames.certified: {
				const arr = [...f?.certified];
				arr.forEach((o: IFilterOption) => {
					o.Selected = val.includes(o.Item);
				});
				store.commit('filters/setCertified', arr);
				break;
			}
			case EFilterNames.model: {
				const arr = [...f?.models];
				arr.forEach((o: IFilterOption) => {
					o.Selected = val.includes(o.Item);
				});
				store.commit('filters/setModels', arr);
				break;
			}
			case EFilterNames.body: {
				const arr = [...f?.bodies];
				arr.forEach((o: IFilterOption) => {
					o.Selected = val.includes(o.Item);
				});
				store.commit('filters/setBodies', arr);
				break;
			}
			case EFilterNames.fuel: {
				const arr = [...f?.fuels];
				arr.forEach((o: IFilterOption) => {
					o.Selected = val.includes(o.Item);
				});
				store.commit('filters/setFuels', arr);
				break;
			}
			case EFilterNames.transmission: {
				const arr = [...f?.transmissions];
				arr.forEach((o: IFilterOption) => {
					o.Selected = val.includes(o.Item);
				});
				store.commit('filters/setTransmissions', arr);
				break;
			}
			case EFilterNames.price: {	
				store.commit('filters/setPrice', val);
				break;
			}
			case EFilterNames.year: {
				store.commit('filters/setYear', val);
				break;
			}
		}
		store.commit('setVehiclesIndex', 0);
		store.dispatch('updateVehicles', { amend: false });
	};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(_unref(panelOpenClass))
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(FiltersToggle),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("strong", null, _toDisplayString(_unref(totalVehicles)), 1),
        _createTextVNode(" " + _toDisplayString(_unref(totalVehiclesText)), 1)
      ]),
      _createElementVNode("div", { class: "clear-filters-wrapper" }, [
        _createElementVNode("button", {
          type: "button",
          class: "button-link",
          onClick: handleNewSearchClick
        }, "New Search")
      ])
    ]),
    (_unref(allowConditionSelection))
      ? (_openBlock(), _createBlock(FilterMultiSelect, {
          key: 0,
          title: "Condition",
          name: _unref(EFilterNames).condition,
          options: _unref(conditionOptions),
          onChange: _cache[0] || (_cache[0] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).condition)))
        }, null, 8, ["name", "options"]))
      : _createCommentVNode("", true),
    allowLocationSelection
      ? (_openBlock(), _createBlock(FilterMultiSelect, {
          key: 1,
          title: "Locations",
          name: _unref(EFilterNames).location,
          options: _unref(locationOptions),
          onChange: _cache[1] || (_cache[1] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).location)))
        }, null, 8, ["name", "options"]))
      : _createCommentVNode("", true),
    _createVNode(FilterMultiSelect, {
      title: "Makes",
      name: _unref(EFilterNames).make,
      options: _unref(makeOptions),
      onChange: _cache[2] || (_cache[2] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).make)))
    }, null, 8, ["name", "options"]),
    (_unref(allowCertifiedSelection))
      ? (_openBlock(), _createBlock(FilterMultiSelect, {
          key: 2,
          title: _unref(certifiedTitle),
          name: _unref(EFilterNames).certified,
          options: _unref(certifiedOptions),
          onChange: _cache[3] || (_cache[3] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).certified)))
        }, null, 8, ["title", "name", "options"]))
      : _createCommentVNode("", true),
    (_unref(allowModelSelection))
      ? (_openBlock(), _createBlock(FilterMultiSelect, {
          key: 3,
          title: "Models",
          name: _unref(EFilterNames).model,
          options: _unref(modelOptions),
          onChange: _cache[4] || (_cache[4] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).model)))
        }, null, 8, ["name", "options"]))
      : _createCommentVNode("", true),
    (_unref(allowBodySelection))
      ? (_openBlock(), _createBlock(FilterMultiSelect, {
          key: 4,
          title: "Body Type",
          name: _unref(EFilterNames).body,
          options: _unref(bodyOptions),
          closedDefault: false,
          onChange: _cache[5] || (_cache[5] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).body)))
        }, null, 8, ["name", "options"]))
      : _createCommentVNode("", true),
    (_unref(allowFuelSelection))
      ? (_openBlock(), _createBlock(FilterMultiSelect, {
          key: 5,
          title: "Engine/Fuel Type",
          name: _unref(EFilterNames).fuel,
          options: _unref(fuelOptions),
          closedDefault: false,
          onChange: _cache[6] || (_cache[6] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).fuel)))
        }, null, 8, ["name", "options"]))
      : _createCommentVNode("", true),
    (_unref(allowTransmissionSelection))
      ? (_openBlock(), _createBlock(FilterMultiSelect, {
          key: 6,
          title: "Transmission",
          name: _unref(EFilterNames).transmission,
          options: _unref(transmissionOptions),
          closedDefault: false,
          onChange: _cache[7] || (_cache[7] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).transmission)))
        }, null, 8, ["name", "options"]))
      : _createCommentVNode("", true),
    (_unref(initialPriceMin) != _unref(initialPriceMax))
      ? (_openBlock(), _createBlock(FilterRange, {
          key: 7,
          title: "Price",
          name: _unref(EFilterNames).price,
          min: _unref(initialPriceMin),
          max: _unref(initialPriceMax),
          "selected-min": _unref(selectedPriceMin),
          "selected-max": _unref(selectedPriceMax),
          step: 1000,
          displayModifier: _unref(getPriceFormat),
          onChange: _cache[8] || (_cache[8] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).price)))
        }, null, 8, ["name", "min", "max", "selected-min", "selected-max", "displayModifier"]))
      : _createCommentVNode("", true),
    (_unref(initialYearMin) != _unref(initialYearMax))
      ? (_openBlock(), _createBlock(FilterRange, {
          key: 8,
          title: "Year",
          name: _unref(EFilterNames).year,
          min: _unref(finalInitialYearMin),
          max: _unref(initialYearMax),
          "selected-min": _unref(selectedYearMin),
          "selected-max": _unref(selectedYearMax),
          step: 1,
          displayModifier: _unref(yearFormatModifier),
          onChange: _cache[9] || (_cache[9] = ($event: any) => (handleFilterChange($event, _unref(EFilterNames).year)))
        }, null, 8, ["name", "min", "max", "selected-min", "selected-max", "displayModifier"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(FiltersToggle, { text: 'View Cars' }),
      _createElementVNode("button", {
        type: "button",
        class: "button-outline",
        onClick: handleNewSearchClick
      }, "Clear Filters")
    ]),
    _createVNode(RouterApp, { onReady: handleRouterAppReady })
  ], 2))
}
}

})